// bd-deepcontext-b4zw — API shapes extracted from ScheduledJobs.tsx so the page
// shell, the form modal and the pure logic modules can share them without
// importing each other. Mirrors ./jobRuns/types.ts.
import type { SelectedDomainInfo } from "../SiteListModal";

export interface SiteList {
  id: string;
  name: string;
  description?: string;
  listType: "include" | "exclude";
  domains: string[];
  companyId: string;
  createdAt: string;
  updatedAt: string;
}

export interface ScheduledJob {
  id: string;
  name: string;
  description?: string;
  urlsPerRun: number;
  runsPerDay: number;
  countryCodes?: string[];
  promptIds: string[];
  siteListId?: string;
  siteList?: SiteList;
  domainExtensions?: string[]; // TLD filter (e.g., ['sk', 'cz'])
  urlSourceMode?: "default" | "classification";
  countryDistributionMode?: "uniform" | "proportional";
  similaritySelectivity?: number;
  isActive: boolean;
  autoSyncManaged?: boolean; // bd-bqw.1 — auto-sync endDate + isActive from DSP campaigns
  /** deepcontext-63k8.3 — self-hosted server id the job runs on; null = Gemini.
   *  Only present for the one user allowed to see the picker. */
  localModelId?: string | null;
  localModelConfigured?: boolean;
  excludeHomepages: boolean;
  hasRunningJob?: boolean;
  startDate?: string;
  endDate?: string;
  lastRunAt?: string;
  nextRunAt?: string;
  createdAt: string;
  updatedAt: string;
  userId?: string;
}

/** deepcontext-63k8.3 — one entry of GET /api/scheduled-jobs/local-models. */
export interface LocalModelServer {
  id: string;
  label: string;
  model: string;
}

export interface ScheduledJobStats {
  totalJobs: number;
  activeJobs: number;
  totalRuns: number;
  successfulRuns: number;
  failedRuns: number;
  pendingRuns: number;
  runningRuns: number;
  dailyUrlsToScan: number;
  estimatedDailyCost: number;
}

export const EMPTY_STATS: ScheduledJobStats = {
  totalJobs: 0,
  activeJobs: 0,
  totalRuns: 0,
  successfulRuns: 0,
  failedRuns: 0,
  pendingRuns: 0,
  runningRuns: 0,
  dailyUrlsToScan: 0,
  estimatedDailyCost: 0,
};

export interface ScheduledJobCreateRequest {
  name: string;
  description?: string;
  urlsPerRun: number;
  runsPerDay: number;
  countryCodes?: string[];
  promptIds: string[];
  selectedDomainInfos?: SelectedDomainInfo[];
  domainExtensions?: string[]; // TLD filter (e.g., ['sk', 'cz'])
  excludeHomepages?: boolean;
  urlSourceMode?: "default" | "classification";
  countryDistributionMode?: "uniform" | "proportional";
  similaritySelectivity?: number;
  startDate: string;
  endDate: string;
  /** deepcontext-c919 — id of the job this one duplicates; the backend
   *  deactivates that job and hands its blueprints over to the new one. */
  duplicateOfJobId?: string;
  /** deepcontext-63k8.3 — sent only when the user may see the picker. */
  localModelId?: string | null;
}

export interface ScheduledJobUpdateRequest {
  name?: string;
  description?: string;
  urlsPerRun?: number;
  runsPerDay?: number;
  countryCodes?: string[];
  promptIds?: string[];
  selectedDomainInfos?: SelectedDomainInfo[];
  domainExtensions?: string[]; // TLD filter (e.g., ['sk', 'cz'])
  isActive?: boolean;
  autoSyncManaged?: boolean; // bd-bqw.1 — opt-in to auto-sync from DSP campaigns
  localModelId?: string | null; // deepcontext-63k8.3 — sent only when the user may see the picker; null clears it
  excludeHomepages?: boolean;
  urlSourceMode?: "default" | "classification";
  countryDistributionMode?: "uniform" | "proportional";
  similaritySelectivity?: number;
  startDate?: string;
  endDate?: string;
}

export interface UrlPoolCountryEstimate {
  urls: number;
  requests: number;
  marketUrls: number;
  marketRequests: number;
}

export interface UrlPoolEstimate {
  total: number;
  totalRequests: number;
  marketTotal: number;
  marketTotalRequests: number;
  byCountry: Record<string, UrlPoolCountryEstimate>;
  coverageStatus?: "ok" | "no_domains_in_country";
  coverage?: {
    status: "ok" | "no_domains_in_country";
    deadCountries: string[];
  };
}

export interface UnmatchedUrl {
  url: string;
  reasoning: string | null;
  confidence: number;
}

export type UnmatchedSort = "lowest" | "highest";
